import React from "react";
import * as moment from "moment";
import { useSelector } from "react-redux";
import ReactToPrint from "react-to-print";
import { Box, Button, Typography } from "@mui/material";

import Barcode from "react-barcode";
import { RootState } from "../../../../store";
import { getCustomerNumber } from "../../ClientList";
import useGetDepartments from "../../../../hooks/department";
import usePrintStyles from "../../../../hooks/usePrintStyles";
import { Client } from "../../../../interfaces/ClientInterface";
import { separateByComma } from "../../../../helpers/formatters";
import { clientFullNameSelector } from "../../../../reducers/client";
import { useClientQueueNumber } from "../../../../hooks/useClientQueueNumber";
import { getSpFullName, getClientAge, getClientGender } from "../StickerUtils";
import useCurrentResourceCentre from "../../../../hooks/useCurrentResourceCentre";
import { ServiceProvider } from "../../../../interfaces/ServiceProvidersInterface";
import { convertADtoBS } from "../../../../components/Calendar/functions/calendarFunctions";
import { rupeesDisplayFull } from "../../../../helpers/util";
import { useShowClientAgeInYearsOnly } from "../../../../hooks/useShowClientAgeInYearsOnly";

// eslint-disable-next-line max-len
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, import/prefer-default-export
export const ClientStickerTemplate2 = ({
  client,
  onPrintClick,
  trigger,
  serviceProviderId
}: {
  client: Client;
  serviceProviderId?: number;

  trigger?: React.ReactElement;
  onPrintClick?: () => void;
}) => {
  const printRef = React.useRef(null);

  // custom hooks
  const resourceCentre = useCurrentResourceCentre();
  const { showClientAgeInYearsOnly } = useShowClientAgeInYearsOnly();
  const departments = useGetDepartments({ showDeletedDepartments: true });
  const { queueNumber, refetch } = useClientQueueNumber({ serviceProviderId });

  usePrintStyles({
    pageStyle: "size: 3.375in 1.75in; margin: 3mm;"
  }).applyPrintStyles();

  // selectors
  const serviceProviders: ServiceProvider[] = useSelector(
    (state: RootState) => state.resources.resourceCentreServiceProviders
  );

  // helpers
  const currentDateAndTime = moment().format();
  const fullName = clientFullNameSelector(client).toUpperCase();
  const BSDate = convertADtoBS(moment.utc(currentDateAndTime.slice(0, 10))).formatted;
  const currentTime = currentDateAndTime.slice(11, 16);

  const spIdToUse = serviceProviderId ?? client?.lastHandledSpId;
  const serviceProvider = serviceProviders.find((sp) => Number(sp.id) === Number(spIdToUse));

  const spDepartment =
    departments.find(({ id }) => serviceProvider?.departmentId === id)?.name || null;
  const isQueueEnabled = resourceCentre?.settings?.queueSettings?.enableQueueSystem;
  const defaultOpdFee = resourceCentre?.settings?.opdSettings?.defaultOpdFee ?? "";
  const defaultAdvice = resourceCentre?.settings?.opdSettings?.defaultAdvice ?? "";

  return (
    <>
      <ReactToPrint
        trigger={() => trigger ?? <Button variant="contained">Print</Button>}
        content={() => printRef.current}
        onBeforePrint={onPrintClick}
        onAfterPrint={() => {
          if (isQueueEnabled) {
            refetch();
          }
        }}
      />

      <Box component="iframe" className="displayContents" title="printIframe">
        <Box
          ref={printRef}
          sx={{
            height: "100%",
            fontFamily: "Verdana",
            p: "2mm",
            display: "flex",
            flexDirection: "column",
            gap: 1
          }}
        >
          {/* rc name and date */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: "1.75mm", fontWeight: 700 }}>
              {resourceCentre?.name || ""}
            </Typography>
            <Typography sx={{ fontSize: "1.75mm" }}>
              {BSDate} BS {currentTime}
            </Typography>
          </Box>

          {/* Detail Rows */}
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            {/* Left Column */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.2 }}>
              <Typography sx={{ fontSize: "1.75mm" }}>
                <span>Customer ID : </span>
                <b>{getCustomerNumber(client.customerNumber)}</b>
              </Typography>
              <Typography sx={{ fontSize: "1.75mm" }}>
                <span>Patient Name : </span>
                <b>{fullName}</b>
              </Typography>
              <Typography sx={{ fontSize: "1.75mm" }}>
                <span>Age/Sex :</span>{" "}
                {client?.dob ? getClientAge(client.dob, showClientAgeInYearsOnly) : ""}/
                {getClientGender(client.gender)}
              </Typography>
              {(client?.address || client?.city) && (
                <Typography sx={{ fontSize: "1.75mm" }}>
                  <span>Address :</span> {separateByComma(client.address, client.city)}
                </Typography>
              )}
              <Typography sx={{ fontSize: "1.75mm" }}>
                <span>Department:</span> {spDepartment || "OPD"}
              </Typography>
              <Typography sx={{ fontSize: "1.75mm" }}>
                <span>Consultant :</span> {serviceProvider ? getSpFullName(serviceProvider) : "N/A"}
              </Typography>

              {/* opd fee */}
              {defaultOpdFee && (
                <Typography sx={{ fontSize: "1.75mm" }}>
                  <span>OPD Fee :</span> {rupeesDisplayFull(defaultOpdFee)}
                </Typography>
              )}

              {/* default advice */}
              {defaultAdvice && (
                <Typography sx={{ fontSize: "1.75mm" }}>
                  <span>Advice :</span> {defaultAdvice}
                </Typography>
              )}
            </Box>

            {/* Right Column */}
            {isQueueEnabled && (
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: "1.75mm", textAlign: "right" }}>
                  <span>Token No. :</span> {queueNumber}
                </Typography>
              </Box>
            )}
          </Box>

          {/* footer */}
          <Box>
            <Barcode
              value={String(client.id)}
              height={15}
              width={1.2}
              displayValue={false}
              margin={0}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};
